import { v4 as uuidv4 } from "uuid";
import Auth from "opensooq-sso";

const TOKEN_MESSAGE_TYPE = "ssoToken";

const getSource = () => {
    try {
        const regexp = /iPhone|iPad|iPod|Android/i;
        const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
        return regexp.test(userAgent) ? "mobile" : "desktop";
    } catch {
        return "desktop";
    }
};

/**
 * JWT string for `Authorization: Bearer` on MLS API requests.
 *
 * MLS login sets `Authorization` as **HttpOnly** — JS cannot read it; the browser
 * still sends it on `fetch(..., { credentials: 'include' })` when origin / cookie
 * rules allow (same site or configured `SameSite` / `Domain`).
 *
 * When the cookie is readable (e.g. non-HttpOnly in dev), we read it here so the
 * Bearer header matches `authenticate` middleware.
 *
 * Fallbacks: `window.MLS_AUTH_TOKEN`, then `?mls_token=` / `?access_token=`.
 */
export const getMlsAuthToken = () => {
  try {
    if (typeof window !== "undefined" && window.MLS_AUTH_TOKEN) {
      const t = String(window.MLS_AUTH_TOKEN).trim();
      if (t) {
        return t;
      }
    }
    if (typeof window === "undefined" || !window.location?.search) {
      return null;
    }
    const params = new URLSearchParams(window.location.search);
    const fromQuery =
      params.get("mls_token") || params.get("access_token");
    if (fromQuery) {
      return fromQuery.trim();
    }
  } catch {
    // ignore
  }
  return null;
};

export const getLocale = () => {
    try {
        const stored = typeof localStorage !== "undefined"
            ? localStorage.getItem("flutter.lang_code")
            : null;
        if (stored === "ar" || stored === "en") {
            return stored;
        }
        const fallback = typeof navigator !== "undefined"
            ? (navigator.language || navigator.userLanguage || "en")
            : "en";
        return fallback.startsWith("ar") ? "ar" : "en";
    } catch {
        return "en";
    }
};

const getUUID = () => {
    return uuidv4();
};

const buildTokenPayload = (token) => ({
    type: TOKEN_MESSAGE_TYPE,
    token,
});

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getParentOrigin = () => {
    try {
        if (typeof document === "undefined") {
            return "*";
        }
        if (!document.referrer) {
            return "*";
        }
        return new URL(document.referrer).origin;
    } catch {
        return "*";
    }
};

const postMessageToParent = (payload) => {
    try {
        if (typeof window === "undefined") {
            return false;
        }
        if (!window.parent || window.parent === window) {
            return false;
        }
        window.parent.postMessage(payload, getParentOrigin());
        return true;
    } catch {
        return false;
    }
};

const callFlutterHandler = async (payload) => {
    try {
        if (typeof window === "undefined") {
            return false;
        }
        const handler = window.flutter_inappwebview?.callHandler;
        if (typeof handler !== "function") {
            return false;
        }
        await handler(TOKEN_MESSAGE_TYPE, payload);
        return true;
    } catch (error) {
        console.warn("SSO token handler failed", error);
        return false;
    }
};

const notifyFlutterToken = async (token) => {
    if (!token) {
        return false;
    }
    const payload = buildTokenPayload(token);
    let handlerResult = await callFlutterHandler(payload);
    if (!handlerResult) {
        const deadline = Date.now() + 5000;
        while (Date.now() < deadline && !handlerResult) {
            await delay(200);
            handlerResult = await callFlutterHandler(payload);
        }
    }
    const postResult = postMessageToParent(payload);
    return handlerResult || postResult;
};

export const openLogin = async (locale) => {
    try {
        const auth = Auth.getInstance();
        if (!auth.initCalled) {
            await auth.init(
                process.env.REACT_APP_SSO_DOMAIN || "sooqdev2.com",
                "sso",
                locale,
                "jo", // TODO: STATIC JO FOR NOW
                getSource(),
                getUUID(),
                59 // TODO: STATIC AMMAN FOR NOW,
            );
        }

        const _state = await auth.getState();

        let _token = 'unset';

        await delay(100);

        _token = await auth.getToken(true, true, (a) => { });

        await notifyFlutterToken(_token);

        return _token;
    } catch (error) {
        console.warn("ZZZ - 2 - SSO openLogin failed", error);
        throw error;
    }
}
